<template>
    <div class="change-payment" data-testid="change-payment" :class="parentClass">
        <p class="change-date" @click="showChangeDateBox" :class="linkObject">Change date</p>
        <div class="change-payment-due-date" :class="bodyObject">
            <div>
                <div class="advice-box">
                    We provide the flexibility for you to update your next payment for a few days without fee. Please be advised that failure to make payment will result in your account being placed in collection.
                </div>

                <br v-if="!hasError()">

                <div :class="{ 'input-has-error': hasError()}">

                    <div class="error-message" v-if="hasError()">
                        {{ error }}
                    </div>

                    <div v-if="successMessage.length" class="success-message">
                        {{ successMessage }}
                    </div>

                    <input 
                        type="date"
                        tabindex="19"
                        id="next_pay_date"
                        :min="minPaymentDate()"
                        :max="maxPaymentDate()"
                        name="next_pay_date"
                        placeholder="MM/DD/YYYY"
                        class="w-full dropdown-select border border-gray-300 text-base"
                        style="line-height: 16.94px !important;"
                        :value="newDate"
                        @input="onDateInput" />
                </div>
            </div>
            <div class="gap-2 my-4">
                <div class="flex justify-between" v-if="!saving">
                    <button 
                        v-if="canMoveToEnd()"
                        data-testid="move-to-end"
                        class="border border-primary-700 rounded text-primary-700 text-base font-semibold p-2"
                        @click.prevent="onMoveToEndClick">
                            Move to end
                    </button>

                    <div v-else>&nbsp;</div>
                    
                    <div class="flex itens-end gap-2">
                        <h6 class="cursor-pointer text-base font-semibold" data-testid="cancel-change-due-date" @click="onCancelClick">Cancel</h6>
                        <a v-if="newDate != paymentDueDate" @click="onSaveClick" data-testid="save-change-due-date" class="cursor-pointer font-semibold no-underline text-sm">Save</a>
                    </div>
                </div>
                <div class="text-right" v-else>
                    <i class="fa fa-circle-notch fa-spin" />
                </div>
            </div>
        </div>
        <AppModal :show-modal="openModal" @close="handleCloseModal" :use-min-height="false">
            <div>
                <p>{{ moveToEndWarn() }}</p>
                <br>
                <button 
                    data-testid="cancel-move-to-end"
                    class="border border-primary-700 rounded text-primary-700 text-base font-semibold p-2 pl-4 pr-4"
                    @click.prevent="onCancelMoveToEnd">
                        No
                </button>
                <button 
                    data-testid="confirm-move-to-end"
                    class="bg-primary border border-primary-700 rounded text-white text-base font-semibold p-2 pl-4 pr-4"
                    @click.prevent="onConfirmMoveToEnd">
                        Yes
                </button>
            </div>
        </AppModal>
    </div>
</template>

<script>

import moment from 'moment';
import AppModal from './AppModal';

export default {
    name: "ChangePaymentDueDate",
    components: {
        AppModal,
    },
    props: {
        payment: {
            type: Object,
            required: true,
        },
        numberOfMovedPaymentsToEnd: {
            type: Number | null,
            required: true,
        },
        frequency: {
            type: String,
            required: true,
        },
    },
    data() {
        return {
            open: false,
            saving: false,
            newDate: '',
            paymentDueDate: '',
            error: '',
            openModal: false,
            successMessage: '',
        };
    },
    methods: {

        hasError() {
            return this.error.length > 0;
        },

        minPaymentDate() {
            return moment.utc(this.payment.due_at).add(2, 'days').format('YYYY-MM-DD');
        },

        maxPaymentDate() {
            if(this.frequency === 'bi-weekly') {
                return moment.utc(this.payment.due_at).add(7, 'days').format('YYYY-MM-DD');
            } else if(this.frequency === 'twice_monthly') {
                return moment.utc(this.payment.due_at).add(15, 'days').format('YYYY-MM-DD');
            }

            return moment.utc(this.payment.due_at).add(15, 'days').format('YYYY-MM-DD');
        },

        hideError() {
            this.error = '';
        },

        showError(message) {
            this.error = message;
        },

        handleCloseModal() {
            this.openModal = false;
        },

        onDateInput(e) {
            this.changeDate(e);
            window.LogRocket?.track?.('payment_date_changed', {
                loan_id: this.payment.loan_id,
                old_date: this.paymentDueDate,
                new_date: e.target.value
            });
        },

        changeDate(e) {
            this.hideError();
            this.newDate = e.target.value;
        },

        onMoveToEndClick() {
            window.LogRocket?.track?.('move_to_end_clicked', {
                loan_id: this.payment.loan_id,
                current_date: this.paymentDueDate
            });
            this.openWarnModal();
        },

        onCancelClick() {
            window.LogRocket?.track?.('change_payment_date_cancelled', {
                loan_id: this.payment.loan_id
            });
            this.cancel();
        },

        onSaveClick() {
            window.LogRocket?.track?.('change_payment_date_saved', {
                loan_id: this.payment.loan_id,
                old_date: this.paymentDueDate,
                new_date: this.newDate
            });
            this.save();
        },

        onCancelMoveToEnd() {
            window.LogRocket?.track?.('move_to_end_cancelled', {
                loan_id: this.payment.loan_id
            });
            this.cancelMoveToEndPayment();
        },

        onConfirmMoveToEnd() {
            window.LogRocket?.track?.('move_to_end_confirmed', {
                loan_id: this.payment.loan_id,
                new_date: this.maxPaymentDate()
            });
            this.confirmMoveToEndPayment();
        },

        showChangeDateBox() {
            // Track using optional chaining for safety
            window.LogRocket?.track?.('change_payment_date_opened', {
                loan_id: this.payment.loan_id,
                current_date: this.paymentDueDate
            });

            this.hideError();
            this.open = true;
        },

        canMoveToEnd() {
            if(this.numberOfMovedPaymentsToEnd === null) {
                return false;
            }

            return this.numberOfMovedPaymentsToEnd < 3;
        },

        showSuccessMessage(message, reload = true) {
            this.successMessage = message;
            setTimeout(() => {
                if(reload) {
                    window.location.reload();
                } else {
                    this.successMessage = '';
                }
            }, 5000);
        },

        openWarnModal() {
            this.openModal = true;
        },

        cancel() {
            this.hideError();
            this.open = false;
        },

        cancelMoveToEndPayment() {
            this.openModal = false;
        },

        confirmMoveToEndPayment() {
            this.openModal = false;
            this.moveToEnd();
        },

        moveToEndWarn() {
            if(this.numberOfMovedPaymentsToEnd == 0) {
                return 'You can only move one payment to the end of your loan without penalty. Are you sure you want to move this one payment?';
            }
            
            return 'Since you\'ve already missed a payment, you will incur a $15,00 fee to move this payment.  Are you sure you want to move this payment?';
        },

        moveToEnd() {

            if(this.saving) {
                return;
            }

            this.saving = true;

            this.hideError();
            axios.post('/account/payment/move-to-end', {
                payment_id: this.payment.id,
            })
            .then(response => {
                this.showSuccessMessage('Payment was moved to the end of the loan successfuly.');
            })
            .catch(error => {
                
                this.handleError(error);

                this.saving = false;
            });
        },

        save() {

            if(this.saving) {
                return;
            }

            this.saving = true;

            this.hideError();

            axios.post('/account/payment/change-due-date', {
                payment_id: this.payment.id,
                due_at: this.newDate,
            })
            .then(response => {
                this.saving = false;
                this.due_at = this.newDate;
                this.$emit('due-date-changed', this.payment);
                this.showSuccessMessage('Payment due date was changed successfuly.');
            })
            .catch(error => {
                
                this.handleError(error);

                this.saving = false;
            });
        },

        handleError(error) {
            if (error.response) {
                if (error.response.data.error) {
                    this.showError(error.response.data.error);
                } else {
                    this.showError('An error occurred. Please try again.'); 
                }

            } else if (error.request) {
                console.log('Error request:', error.request);
                this.showError('No response from server. Please check your connection.');
            } else {
                console.log('Error message:', error.message);
                this.showError('An error occurred. Please try again.');
            }
        },
    },

    mounted() {
        this.newDate = this.paymentDueDate = this.payment.due_at ? moment.utc(this.payment.due_at).format('YYYY-MM-DD') : '';
    },

    computed: {
        bodyObject() {
            return {
                "d-block": this.open,
                "d-none": !this.open,
            };
        },
        linkObject() {
            return {
                "d-none": this.open,
            };
        },
        parentClass() {
            return {
                "order-2": !this.open,
                "order-4": this.open,
            };
        },
    },
};
</script>

<style scoped>

.change-date {
    cursor: pointer;
    color: green;
    padding: 10px;
    display: inline-block;
    border: 1px solid #00b700;
    border-radius: 5px;
}

.success-message {
    padding: 10px;
    border: 1px solid #00b700;
    border-radius: 5px;
    color: green;
    margin: 10px 0;
}

.error-message {
    padding: 10px 10px 5px;
    border: 1px solid red;
    border-radius: 5px;
    margin: 10px 0;
}
.change-payment-due-date {
    transition: all 0.3s ease-in-out;
}

.advice-box {
    padding: 15px;
    border: 1px solid #dedede;
    background: #eee;
    border-radius: 5px;
}

.change-payment {
    flex-grow: 1;
    margin-left: 10px;
}

.order-2 {
    order: 2;
}

.order-4 {
    order: 4;
    flex-basis: 100%;
    margin: 15px 0;
}

.d-block {
    display: block;
}
.d-none {
    display: none;
}
</style>
